import type { TemplateStep } from '../types'

export interface AggregatedStepResources {
  tools: Array<{ name: string; required: boolean }>
  materials: Array<{
    name: string
    cost?: number
    quantity?: number
    unit?: string
  }>
}

/**
 * Deduplicated union of tools and materials across all steps (name-keyed),
 * in step iteration order.
 */
export function aggregateToolsAndMaterialsFromSteps(
  steps: Array<TemplateStep>
): AggregatedStepResources {
  const toolsMap = new Map<string, { name: string; required: boolean }>()
  const materialsMap = new Map<
    string,
    { name: string; cost?: number; quantity?: number; unit?: string }
  >()

  for (const step of steps) {
    for (const tool of step.tools ?? []) {
      const name = tool.name ?? tool.customName
      if (!name) continue
      const existing = toolsMap.get(name)
      if (!existing) {
        toolsMap.set(name, { name, required: tool.required })
      } else if (tool.required) {
        toolsMap.set(name, { name, required: true })
      }
    }

    for (const material of step.materials ?? []) {
      const name = material.name ?? material.customName
      if (!name) continue
      // Later steps win on duplicate names (same as re-patching tool `required` truth)
      materialsMap.set(name, {
        name,
        cost: material.estimatedCost,
        quantity: material.quantity,
        unit: material.unit,
      })
    }
  }

  return {
    tools: Array.from(toolsMap.values()),
    materials: Array.from(materialsMap.values()),
  }
}
